"use client";

import { useEffect, useRef } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { GADS_ID, adsConversion, readConsent } from "@/lib/gtag";

type GtagWindow = Window & {
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
  __rbGtag?: boolean;
};

/**
 * Loads the Google tag (Ads conversion measurement) with Consent Mode v2.
 * The consent default is pushed onto the dataLayer BEFORE gtag.js is appended,
 * so nothing is stored client-side unless the visitor has accepted the banner
 * (ConsentBanner calls grantConsent, which issues the consent update).
 *
 * Also fires the signup conversion: the signUp action redirects with a
 * one-time `?signup=1`, which we consume and strip here.
 */
export function GoogleTag() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  const firedSignup = useRef(false);

  useEffect(() => {
    const w = window as GtagWindow;
    if (!GADS_ID || w.__rbGtag) return;
    w.__rbGtag = true;

    w.dataLayer = w.dataLayer || [];
    // gtag must push the arguments object itself, not an args array.
    // eslint-disable-next-line prefer-rest-params
    w.gtag = function gtag() { w.dataLayer!.push(arguments); } as (...a: unknown[]) => void;

    w.gtag("consent", "default", {
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
      analytics_storage: "denied",
      wait_for_update: 500,
    });
    w.gtag("set", "ads_data_redaction", true);
    if (readConsent() === "granted") {
      w.gtag("consent", "update", {
        ad_storage: "granted",
        ad_user_data: "granted",
        ad_personalization: "granted",
        analytics_storage: "granted",
      });
    }
    w.gtag("js", new Date());
    w.gtag("config", GADS_ID);

    const s = document.createElement("script");
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GADS_ID}`;
    s.async = true;
    document.head.appendChild(s);
  }, []);

  // Signup conversion: consume the one-time marker from the post-signup redirect.
  useEffect(() => {
    if (searchParams.get("signup") !== "1" || firedSignup.current) return;
    firedSignup.current = true;
    adsConversion("signup");
    const rest = new URLSearchParams(searchParams);
    rest.delete("signup");
    const qs = rest.toString();
    router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
  }, [searchParams, pathname, router]);

  return null;
}
